import { Button, Dialog, Text } from '@risksmart-app/atomic-ui';
import type { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  /** Names the organisation in the title; falls back to a generic title. */
  organisationName?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const CONSEQUENCE_KEYS = [
  'modules.fields.RecalculateRiskScores_confirm_consequence_delete',
  'modules.fields.RecalculateRiskScores_confirm_consequence_thresholds',
  'modules.fields.RecalculateRiskScores_confirm_consequence_notifications',
] as const;

/**
 * Blast-radius confirmation for the org-wide recalculation (RSP-7298).
 * Follows the destructive-confirmation treatment already in the app
 * (`DeleteImpactConfirmationDialog`): medium dialog, intro plus a bulleted
 * list of consequences with the key phrase emphasised, destructive confirm
 * button first.
 */
export const RecalculateRiskScoresDialog: FC<Props> = ({
  open,
  organisationName,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'modules.fields' });
  const { t: ct } = useTranslation('common');

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
      size={'md'}
    >
      <Dialog.Header
        title={
          organisationName
            ? t('RecalculateRiskScores_confirm_header_named', {
                organisation: organisationName,
              })
            : t('RecalculateRiskScores_confirm_header')
        }
      />
      <Dialog.Body className={'flex flex-col gap-3'}>
        <div
          data-testid={'recalculate-risk-scores-modal'}
          className={'flex flex-col gap-3'}
        >
          <Text preset={'body-md'}>
            <Trans
              i18nKey={'modules.fields.RecalculateRiskScores_confirm_intro'}
              ns={'common'}
              components={{ strong: <strong /> }}
            />
          </Text>
          <ul className={'list-disc pl-6 flex flex-col gap-1'}>
            {CONSEQUENCE_KEYS.map((key) => (
              <li key={key}>
                <Trans
                  i18nKey={key}
                  ns={'common'}
                  components={{ strong: <strong /> }}
                />
              </li>
            ))}
          </ul>
        </div>
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          data-testid={'recalculate-risk-scores-confirm-button'}
          variant={'destructive'}
          onClick={onConfirm}
        >
          {t('RecalculateRiskScores_confirm_button')}
        </Button>
        <Button variant={'neutral'} style={'outline'} onClick={onCancel}>
          {ct('cancel')}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
